import { Link } from "react-router-dom";
import {
  TrendingUp,
  ShieldCheck,
  Banknote,
  CheckCircle2,
  ArrowRight,
  LockKeyhole,
  Users,
  FileCheck2,
  BarChart3,
  Landmark,
  Wallet,
  MessageCircle,
} from "lucide-react";

import "../assets/css/investment.css";

import growthImg from "../assets/img/growth-success3.png";
import modelImg from "../assets/img/growth-success1.png";
import planningImg from "../assets/img/financial-planning1.png";

const benefits = [
  "Structured lending support for individuals and businesses",
  "Transparent lending process from enquiry to approval",
  "Salary earners, SMEs, traders, and business owners supported",
  "Collateral-backed and risk-managed lending options",
  "Clear repayment guidance and customer support",
  "Customers can contact our team for suitable lending plans",
];

const protection = [
  {
    icon: FileCheck2,
    title: "Strict Customer Screening",
    text: "Every lending request is reviewed carefully before approval.",
  },
  {
    icon: LockKeyhole,
    title: "Secured Lending",
    text: "We use structured checks, documentation, and risk controls.",
  },
  {
    icon: BarChart3,
    title: "Repayment Monitoring",
    text: "Loans are monitored with organized repayment and reporting systems.",
  },
];

export default function Investment() {
  return (
    <main className="investment-page">
      {/* HERO */}
      <section className="investment-hero">
        <div className="investment-hero-copy">
          <span>Lending Plans</span>
          <h1>Access flexible lending plans built around your needs</h1>
          <p>
            GMT Technologies Limited provides structured lending solutions for
            salary earners, SMEs, traders, and growing businesses. Our team helps
            customers choose suitable loan options with transparency, security,
            and repayment confidence.
          </p>

          <div className="investment-hero-actions">
            <Link to="/contact" className="primary-invest-btn">
              Speak With Us <ArrowRight size={16} />
            </Link>

            <Link to="/services" className="secondary-invest-btn">
              View Lending Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="investment-hero-image">
          <img src={growthImg} alt="GMT Technologies lending support" />

          <div className="hero-invest-card top">
            <strong>Flexible</strong>
            <span>Lending Plans</span>
          </div>

          <div className="hero-invest-card bottom">
            <strong>12 Months</strong>
            <span>Available Tenor</span>
          </div>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="investment-features">
        <div className="investment-feature-card">
          <TrendingUp />
          <h3>Flexible Loan Options</h3>
          <p>
            Lending support designed for personal, salary-backed, and business
            finance needs.
          </p>
        </div>

        <div className="investment-feature-card active">
          <ShieldCheck />
          <h3>Customer Protection</h3>
          <p>
            Clear requirements, proper verification, and responsible lending
            guidance.
          </p>
        </div>

        <div className="investment-feature-card">
          <Banknote />
          <h3>Structured Repayment</h3>
          <p>
            Repayment plans are organized to help customers manage obligations
            confidently.
          </p>
        </div>
      </section>

      {/* CONTACT INSTEAD OF TABLE */}
      <section className="investment-table-section">
        <div className="investment-section-heading">
          <span>Lending Enquiry</span>
          <h2>Contact our team for available lending plans</h2>
          <p>
            Lending terms, eligible amount, tenor, and repayment structure depend
            on customer profile, income, business type, and documentation.
            Please contact GMT Technologies Limited for proper assessment and
            guidance.
          </p>

          <Link to="/contact" className="primary-invest-btn">
            Request Lending Information <MessageCircle size={16} />
          </Link>
        </div>
      </section>

      {/* BUSINESS MODEL */}
      <section className="investment-model">
        <div className="model-copy">
          <span>Lending Model</span>
          <h2>A simple and effective customer lending structure</h2>
          <p>
            Customers submit enquiries, GMT reviews the request, required
            documents are verified, and eligible customers receive structured
            lending support with clear repayment guidance.
          </p>

          <div className="model-steps">
            <div>
              <strong>01</strong>
              <p>Customer contacts GMT</p>
            </div>
            <div>
              <strong>02</strong>
              <p>Loan need and eligibility are reviewed</p>
            </div>
            <div>
              <strong>03</strong>
              <p>Documents and repayment capacity are verified</p>
            </div>
            <div>
              <strong>04</strong>
              <p>Approved lending plan is processed</p>
            </div>
          </div>
        </div>

        <div className="model-image">
          <img src={modelImg} alt="GMT lending model" />
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="investment-split">
        <div>
          <span className="section-pill">Why Choose Our Lending Plans</span>
          <h2>Structured lending with customer confidence in mind</h2>
          <p>
            Our lending model focuses on transparency, proper documentation,
            customer support, and responsible repayment structures.
          </p>

          <ul className="investment-check-list">
            {benefits.map((item) => (
              <li key={item}>
                <CheckCircle2 size={18} /> {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="highlight-panel">
          <h3>Customer Commitment</h3>
          <p>
            Our goal is to provide lending support that is clear, secure, and
            suitable for each customer’s financial capacity.
          </p>

          <Link to="/contact" className="white-btn">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* RISK MANAGEMENT */}
      <section className="risk-section">
        <div className="investment-section-heading">
          <span>Risk Management</span>
          <h2>Built around responsible lending and repayment reliability</h2>
        </div>

        <div className="risk-grid">
          {protection.map((item) => {
            const Icon = item.icon;

            return (
              <div className="risk-card" key={item.title}>
                <Icon />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FUNDS USAGE */}
      <section className="funds-section">
        <div className="funds-image">
          <img src={planningImg} alt="GMT lending support areas" />
        </div>

        <div className="funds-copy">
          <span>Our Lending Areas</span>
          <h2>We support customers through structured lending activities</h2>

          <div className="funds-list">
            <div>
              <Wallet />
              <p>Salary advance loans</p>
            </div>

            <div>
              <Landmark />
              <p>Asset-backed lending</p>
            </div>

            <div>
              <Users />
              <p>SME and business lending support</p>
            </div>

            <div>
              <ShieldCheck />
              <p>Technology and risk management systems</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="investment-final-cta">
        <span>Start Your Lending Enquiry</span>
        <h2>Need a structured lending plan for personal or business growth?</h2>
        <p>
          Reach out to GMT Technologies Limited today. Our team will guide you
          through available lending options, requirements, and next steps.
        </p>

        <Link to="/contact" className="primary-invest-btn">
          Speak With Our Team <ArrowRight size={16} />
        </Link>
      </section>
    </main>
  );
}